import { storeToRefs } from 'pinia';
import { NavigationDirection, Track } from '../types';
import useRenditionStore from '../stores/rendition';
import useMediaStore from '../stores/media';
import useReaderEngine from '../engine/use-reader-engine';
import useRenderer from '../engine/use-renderer';
import useSmilStore from '../stores/smil';
import { EpubJsSpineItem } from '../types/epubjs';
import useReaderStore from '../stores/reader';
import useSettingStore from '../stores/setting';
import { getTracksForLanguage } from '../util/track-utils';
import useAudioAnnotations from './use-audio-annotations';
import useAudioOnlyReader from '../engine/epubjs/audio-only-reader';

const useAudioTracks = () => {
  const { getCurrentPages, getPageSpineItem, getCFIFromSpineIdentifier, getNeighbouringSpineItem, getIframeByTrack } =
    useReaderEngine();
  const { isContinuous } = useReaderStore();
  const { tracksInPage } = useSmilStore();

  const { getReflowableElementVisibilityInfo } = useRenderer();
  const renditionStore = useRenditionStore();
  const { currentPageFiles } = storeToRefs(renditionStore);
  const { setCurrentPageFilesWithTracks } = renditionStore;
  const mediaStore = useMediaStore();
  const { mainLanguage, preferredLanguage, currentTrack } = storeToRefs(mediaStore);
  const { clearTracks, addTracks, setCurrentTrack, setIsLastTrack, setIsUserChangingTrack } = mediaStore;
  const { hasAudioTracking } = storeToRefs(useSettingStore());
  const { clearPlayingHighlight } = useAudioAnnotations();
  const { buildPlaylist } = useAudioOnlyReader();

  const processTrackVisibilityInReflowable = (track: Track): Track | null => {
    let targetDoc: Document | undefined;
    try {
      targetDoc = getIframeByTrack(track);
    } catch {
      // view not yet loaded — fall back to scanning all iframes
    }
    const visibilityInfo = getReflowableElementVisibilityInfo(track.text.srcFragmentId, targetDoc);

    if (visibilityInfo.position === 'outside-viewport') {
      // outside -> not to be played
      return null;
    }

    if (visibilityInfo.widthShareAfter && track.audio.clipEnd) {
      // partially inside -> turn the page at what we estimate the page-break to be
      return {
        ...track,
        audio: {
          ...track.audio,
          turnPageAt:
            track.audio.clipEnd - (track.audio.clipEnd - (track.audio.clipBegin ?? 0)) * visibilityInfo.widthShareAfter,
        },
      };
    }

    // fully in the viewport
    return track;
  };

  const loadTracks = async (spineItem: EpubJsSpineItem) => {
    try {
      let tracks = await tracksInPage(spineItem);

      // use non-main language if requested
      if (preferredLanguage.value && mainLanguage.value && preferredLanguage.value !== mainLanguage.value) {
        tracks = getTracksForLanguage(tracks, preferredLanguage.value);
      }

      /*
        In Ugglo, a custom book format was used where the default-layout was fixed. When we later
        imported books into Polylino, we forgot to take this into account when setting the layout field in the
        EPUB manifest. Meaning we did never set fixed as layout in the manifest for those books, meaning an EPUB
        reader is likely to rightfully assume they are reflowable - which is what happens here :'(

        This is a hack to identify these books. Ideally, we'd identify them when parsing the manifest. But there doesn't
        seem to be any overly obvious content to base it on :'(
       */
      const isFaultyUggloBookWithoutProperLayoutDefinition =
        tracks.length === 1 && tracks[0].text.srcFragmentId === 'mocked-highlight';

      if (!isContinuous && spineItem.layout === 'reflowable' && !isFaultyUggloBookWithoutProperLayoutDefinition) {
        // change or remove tracks based on text visibility in the viewport
        tracks = tracks
          .map((track) => processTrackVisibilityInReflowable(track))
          .filter((track): track is Track => track !== null);
      }

      if (tracks.length) {
        addTracks(tracks, spineItem.index);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const setCurrentTracks = async () => {
    // Clear old playing highlight before loading new tracks to prevent stuck highlights
    if (currentTrack.value && hasAudioTracking.value) {
      clearPlayingHighlight();
    }

    // For paginated mode: clear all and reload from the visible pages.
    // For continuous mode: DO NOT clear — tracks are accumulated incrementally via
    // hooks.content → loadTracks() → addTracks(). Clearing would permanently destroy
    // tracks for spine items that are buffered by the virtual scroller but not currently
    // on-screen (not returned by viewManager.visible()). Those iframes won't fire
    // hooks.content again, so their tracks would be permanently lost.
    if (!isContinuous) {
      clearTracks();
    }

    const currentPages = getCurrentPages();
    for (let i = 0; i < currentPages.length; i++) {
      const page = currentPages[i];

      await loadTracks(page);
    }

    setCurrentPageFilesWithTracks(currentPageFiles.value);
  };

  const getPlayableElementIds = async (spineItem: EpubJsSpineItem): Promise<string[]> => {
    try {
      const tracks = await tracksInPage(spineItem);

      return tracks.map((track) => track.text.srcFragmentId);
    } catch (error) {
      console.error(error);
    }

    return [];
  };

  const playAudio = async (spineItem: EpubJsSpineItem, elementId?: string) => {
    const tracks = await tracksInPage(spineItem);

    // TODO verify/await tracks have been loaded?

    const requestedTrack = elementId ? tracks.find((t) => t.text.srcFragmentId === elementId) : undefined;
    const track = requestedTrack ?? tracks[0];
    // To avoid playing the same track again from start
    if (currentTrack.value && currentTrack.value?.text.srcFragmentId === track.text.srcFragmentId) {
      return;
    }
    setCurrentTrack(track);
    setIsUserChangingTrack(true);
  };

  /**
   * Finds the index of a target track within an array of tracks.
   * Uses fragment ID and audio clip boundaries for precise matching.
   */
  const getTrackIndex = (tracks: Track[], targetTrack: Track) => {
    return tracks.findIndex(
      (track) =>
        track.text.srcFragmentId === targetTrack.text.srcFragmentId &&
        track.audio.clipBegin === targetTrack.audio.clipBegin &&
        track.audio.clipEnd === targetTrack.audio.clipEnd,
    );
  };

  const loadTracksToMediaStore = (tracks: Track[], spineItemIndex: number): Track[] | null => {
    // In continuous mode, tracks are accumulated incrementally across all buffered spines
    // via hooks.content → loadTracks() → addTracks(). Clearing would permanently destroy
    // tracks for spine items that are buffered but not on-screen.
    if (!isContinuous) {
      clearTracks();
    }

    let tracksToLoad = tracks;

    // use non-main language if requested
    if (preferredLanguage.value && mainLanguage.value && preferredLanguage.value !== mainLanguage.value) {
      tracksToLoad = getTracksForLanguage(tracks, preferredLanguage.value);
    }

    if (!tracksToLoad.length) {
      console.debug('Could not load more tracks');
      return null;
    }

    addTracks(tracksToLoad, spineItemIndex);
    return tracksToLoad;
  };

  /**
   * For background playback, starts playing a track from the given track list (regardless of viewport visibility).
   * @param tracks - The tracks array (should already be loaded)
   * @param startAtIndex - The index of the track we want to start playing.
   */
  const playTracksFromList = async (tracks: Track[], startAtIndex?: number) => {
    if (tracks.length === 0) return;

    const idx = startAtIndex && startAtIndex >= 0 && startAtIndex < tracks.length ? startAtIndex : 0;
    setCurrentTrack(tracks[idx]);
  };

  /**
   * Loads all tracks from the spine item that contains the given track.
   * @param track - The track whose spine should be loaded
   */
  const loadSpineTracksFromTrack = async (track: Track) => {
    const currentTrackCfi = await getCFIFromSpineIdentifier(track.text.src);
    const currentSpine = getPageSpineItem(currentTrackCfi);
    const currentSpineTracks = await tracksInPage(currentSpine);
    loadTracksToMediaStore(currentSpineTracks, currentSpine.index);
  };

  /**
   * Loads tracks from neighboring spine item in given direction regardless of viewport visibility (for background playback).
   * Keeps trying spines until tracks are found, we skip 10 spines, or reach book boundary.
   * @param href - Current spine href
   * @param direction - Direction to search: 'next' or 'prev'
   */
  const loadNeighborSpineTracks = async (href: string, direction: NavigationDirection) => {
    const maxSpineSearchAttempts = 10;
    let attempts = 0;
    let currentHref = href;
    let neighbourSpine: EpubJsSpineItem | null = null;
    let neighbourSpineTracks: Track[];

    do {
      neighbourSpine = getNeighbouringSpineItem(currentHref, direction);

      if (!neighbourSpine) {
        if (direction === 'next') setIsLastTrack(true); // End of book

        return null; //At book boundary - early exit
      }

      neighbourSpineTracks = await tracksInPage(neighbourSpine);
      currentHref = neighbourSpine.path;
      attempts++;
    } while (neighbourSpineTracks.length === 0 && attempts <= maxSpineSearchAttempts);

    const loadedTracks = loadTracksToMediaStore(neighbourSpineTracks, neighbourSpine.index);

    if (!loadedTracks) return null;

    return { tracks: loadedTracks, spineItemIndex: neighbourSpine.index };
  };

  /**
   * Loads tracks from the next spine item in the sequence and starts background playback.
   */
  const loadNextSpineTracksByHrefAndPlay = async (href: string) => {
    const loadResults = await loadNeighborSpineTracks(href, 'next');

    if (!loadResults) return;

    await playTracksFromList(loadResults.tracks);
  };

  /**
   * Loads tracks from the previous spine item in the sequence and starts background playback.
   */
  const loadPrevSpineTracksByHrefAndPlay = async (href: string) => {
    const loadResults = await loadNeighborSpineTracks(href, 'prev');

    if (!loadResults) return;

    await playTracksFromList(loadResults.tracks);
  };

  /**
   * Loads additional tracks for continuous background playback.
   * If the current track is not the last in its spine, loads remaining tracks from the same spine.
   * Otherwise, loads tracks from the next spine item.
   * @param track - Current track to determine what to load next
   */
  const loadContinuousPlaybackTracks = async (track: Track) => {
    const currentTrackCfi = await getCFIFromSpineIdentifier(track.text.src);
    const currentSpine = getPageSpineItem(currentTrackCfi);
    const currentSpineTracks = await tracksInPage(currentSpine);

    const targetTrackIndex = getTrackIndex(currentSpineTracks, track);
    const isFound = targetTrackIndex >= 0;
    const isLast = targetTrackIndex === currentSpineTracks.length - 1;

    //If the given track is not the last track of the spine, load the remaining tracks of the spine & continue playing.
    if (isFound && !isLast) {
      const nextTrackIdx = targetTrackIndex + 1;

      const loadedTracks = loadTracksToMediaStore(currentSpineTracks, currentSpine.index);
      if (!loadedTracks) return;
      await playTracksFromList(loadedTracks, nextTrackIdx);
      return;
    }

    const currentHref = track.text.src.split('#')[0];
    await loadNextSpineTracksByHrefAndPlay(currentHref);
  };

  /** Returns the total narrated duration of the book plus a per-track Map of cumulative book-time, so analytics can report a monotonically increasing position across the book (track clipBegin/clipEnd are per-chapter and reset every audio file).
   * Delegates SMIL pre-load + language filtering to audio-only's buildPlaylist, then flattens its spine-grouped output into the per-track Map our analytics lookup needs.
   */
  const computeBookAudioTimings = async (spineItems: {
    [id: string]: EpubJsSpineItem;
  }): Promise<{ totalDuration: number; trackTimings: Map<string, { start: number; end: number }> }> => {
    const playlist = await buildPlaylist(spineItems);
    const trackTimings = new Map<string, { start: number; end: number }>();
    let cumulative = 0;
    for (const item of playlist) {
      for (const track of item.tracks) {
        const duration = Math.max(0, (track.audio.clipEnd ?? 0) - (track.audio.clipBegin ?? 0));
        const key = `${track.audio.src}|${track.audio.clipBegin ?? ''}|${track.audio.clipEnd ?? ''}`;
        trackTimings.set(key, { start: cumulative, end: cumulative + duration });
        cumulative += duration;
      }
    }
    return { totalDuration: cumulative, trackTimings };
  };

  return {
    setCurrentTracks,
    loadTracks,
    getPlayableElementIds,
    playAudio,
    getTrackIndex,
    loadContinuousPlaybackTracks,
    loadNextSpineTracksByHrefAndPlay,
    loadPrevSpineTracksByHrefAndPlay,
    loadSpineTracksFromTrack,
    loadNeighborSpineTracks,
    computeBookAudioTimings,
    playTracksFromList,
  };
};

export default useAudioTracks;
